/**
 * Status Badge Component - The Intelligent Standard
 *
 * A refined badge for displaying status information
 * with appropriate semantic coloring.
 */

import { ReactNode } from 'react';

export type BadgeVariant =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral'
  | 'primary';

export interface StatusBadgeProps {
  variant?: BadgeVariant;
  children: ReactNode;
  size?: 'sm' | 'md';
  dot?: boolean;
}

export function StatusBadge({
  variant = 'neutral',
  children,
  size = 'md',
  dot = false,
}: StatusBadgeProps) {
  const variantStyles = {
    success: 'bg-success/10 text-success border-success/20',
    warning: 'bg-warning/10 text-warning border-warning/20',
    error: 'bg-error/10 text-error border-error/20',
    info: 'bg-info/10 text-info border-info/20',
    neutral: 'bg-cream text-slate border-cream-dark',
    primary: 'bg-meridian/10 text-meridian border-meridian/20',
  };

  const dotColors = {
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-error',
    info: 'bg-info',
    neutral: 'bg-slate',
    primary: 'bg-meridian',
  };

  const sizeStyles = {
    sm: 'px-2 py-0.5 text-caption',
    md: 'px-3 py-1 text-body-sm',
  };

  return (
    <span
      className={`
        inline-flex items-center gap-1.5
        font-medium rounded-full border
        ${variantStyles[variant]}
        ${sizeStyles[size]}
      `}
    >
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full ${dotColors[variant]}`} />
      )}
      {children}
    </span>
  );
}

/**
 * Status indicator - just the dot
 */
export function StatusDot({ variant = 'neutral' }: { variant?: BadgeVariant }) {
  const colors = {
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-error',
    info: 'bg-info',
    neutral: 'bg-slate',
    primary: 'bg-meridian',
  };

  return (
    <span
      className={`
        inline-block w-2 h-2 rounded-full
        ${colors[variant]}
      `}
    />
  );
}
